import React, { useState, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useLocation, Link } from "wouter";
import { useSubmitBusiness } from "@workspace/api-client-react";
import { useToast } from "@/hooks/use-toast";
import { Briefcase, Info, Send, Phone, Mail, Globe, MapPin, CheckCircle, Tag, CalendarDays, ChevronRight, ChevronLeft } from "lucide-react";
import { useCategories } from "@/hooks/use-categories";
import { TownSelect } from "@/components/TownSelect";
import { ImageUpload } from "@/components/ImageUpload";

export default function SubmitBusiness() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const { mutateAsync: submitBusiness, isPending } = useSubmitBusiness();
  const { toast } = useToast();

  const [name, setName] = useState("");
  const categories = useCategories("business");
  const [category, setCategory] = useState("");
  const [wantsOrdering, setWantsOrdering] = useState(false);
  const [description, setDescription] = useState("");
  const [town, setTown] = useState("Ayr");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [imageUploading, setImageUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!category && categories.length > 0) setCategory(categories[0]!);
  }, [categories]);

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-[70vh] bg-slate-50 px-4">
        <div className="w-full max-w-sm bg-white rounded-3xl border border-slate-100 shadow-sm p-7 text-center space-y-5">
          <div className="w-16 h-16 rounded-2xl bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center mx-auto">
            <Briefcase size={28} className="text-amber-500" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-800">List Your Business</h2>
            <p className="text-sm text-slate-500 mt-2 leading-relaxed">
              You need to be signed in to list your business on Ayrshire Hub.
            </p>
          </div>
          <Link href="/signup?role=business">
            <button className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold py-4 rounded-2xl transition-colors">
              Create Business Account
            </button>
          </Link>
          <Link href="/login">
            <button className="w-full border border-slate-200 text-slate-600 py-3.5 rounded-2xl text-sm hover:bg-slate-50 transition-colors">
              Already have an account? Sign in
            </button>
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !category || !town) {
      toast({ title: "Missing fields", description: "Please fill in your business name, category, and town.", variant: "destructive" });
      return;
    }
    try {
      await submitBusiness({ data: { name, category, description, town, address, phone, email, website, imageUrl: imageUrl || undefined, wantsOrdering } as never });
      setSubmitted(true);
    } catch {
      toast({ title: "Error", description: "Could not submit your business listing. Please try again.", variant: "destructive" });
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-start justify-center px-4 py-12">
        <div className="w-full max-w-sm space-y-5">
          <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-7 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
              <CheckCircle size={32} className="text-emerald-500" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">Listing Submitted!</h2>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">
                <strong>{name}</strong> has been submitted for review. We'll usually approve it within 24 hours.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-4">
            <p className="text-sm font-bold text-slate-700">What happens next?</p>
            {[
              { Icon: CheckCircle, color: "#059669", text: "Your listing goes live once approved by our team" },
              { Icon: Tag, color: "#f0a500", text: "Post exclusive deals to attract local customers" },
              { Icon: CalendarDays, color: "#1e3a5f", text: "Add events to promote what's on at your venue" },
            ].map(({ Icon, color, text }) => (
              <div key={text} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}15` }}>
                  <Icon size={15} style={{ color }} />
                </div>
                <p className="text-sm text-slate-600 leading-relaxed pt-1">{text}</p>
              </div>
            ))}
            <p className="text-xs text-slate-400 leading-relaxed">
              Once approved, manage everything from your{" "}
              <button onClick={() => setLocation("/business")} className="text-primary font-semibold hover:underline">Business Portal</button>
              {" "}— deals, events, and your listing details.
            </p>
          </div>

          <button
            onClick={() => setLocation("/business")}
            className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold py-4 rounded-2xl transition-colors"
          >
            Go to Business Portal <ChevronRight size={16} />
          </button>
          <button
            onClick={() => setLocation("/businesses")}
            className="w-full border border-slate-200 text-slate-600 py-3.5 rounded-2xl text-sm hover:bg-slate-50 transition-colors"
          >
            Browse Business Directory
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-amber-500 px-4 py-6">
        <div className="max-w-xl mx-auto">
          <button onClick={() => window.history.back()} className="flex items-center gap-1 text-sm text-white/80 hover:text-white mb-3 transition-colors">
            <ChevronLeft size={18} /> Back
          </button>
        </div>
        <div className="max-w-xl mx-auto flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <Briefcase size={22} className="text-white" />
          </div>
          <div>
            <h1 className="text-white font-bold text-xl">List Your Business</h1>
            <p className="text-white/80 text-sm">Logged in as {user.name}</p>
          </div>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-4 py-6 space-y-6">
        <div className="flex items-start gap-2.5 bg-primary/5 border border-primary/20 rounded-xl px-4 py-3">
          <Info size={14} className="text-primary flex-shrink-0 mt-0.5" />
          <p className="text-sm text-primary leading-relaxed">
            Your listing will be reviewed by our team before going live. This usually takes less than 24 hours.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Section title="Business Details">
            <FormField label="Business Name *" required>
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. The Harbour Café"
                className="w-full text-sm text-slate-800 placeholder:text-slate-400 outline-none"
              />
            </FormField>
            <div className="h-px bg-slate-100 ml-4" />
            <FormField label="Description">
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Tell customers about your business..."
                rows={3}
                className="w-full text-sm text-slate-800 placeholder:text-slate-400 outline-none resize-none leading-relaxed"
              />
            </FormField>
          </Section>

          <Section title="Category">
            <div className="px-4 py-3">
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setCategory(cat)}
                    className="px-3.5 py-1.5 rounded-full border text-sm font-medium transition-colors"
                    style={{
                      backgroundColor: category === cat ? "#f0a500" : "var(--color-card)",
                      borderColor: category === cat ? "#f0a500" : "#e2e8f0",
                      color: category === cat ? "var(--color-card)" : "#64748b",
                    }}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          </Section>

          <Section title="Location">
            <div className="px-4 py-3">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Town</p>
              <TownSelect value={town} onChange={setTown} className="mb-1" />
            </div>
            <div className="h-px bg-slate-100 ml-4" />
            <FormField label="Street Address" icon={<MapPin size={14} className="text-slate-400" />}>
              <input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="123 High Street, Ayr"
                className="w-full text-sm text-slate-800 placeholder:text-slate-400 outline-none"
              />
            </FormField>
          </Section>

          <Section title="Business Image">
            <div className="px-4 py-3">
              <p className="text-xs text-slate-400 mb-3">Upload your own photo or choose a free stock image from our gallery.</p>
              <ImageUpload
                value={imageUrl}
                onChange={setImageUrl}
                onUploadingChange={setImageUploading}
                label="Upload Image"
                stockCategory="business"
              />
            </div>
          </Section>

          <Section title="Contact Details">
            <p className="text-xs text-slate-400 px-4 pt-3 -mb-1 leading-relaxed">
              These will be displayed on your business listing so customers can reach you.
            </p>
            <FormField label="Phone Number" icon={<Phone size={14} className="text-slate-400" />}>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="01292 265000"
                className="w-full text-sm text-slate-800 placeholder:text-slate-400 outline-none"
              />
            </FormField>
            <div className="h-px bg-slate-100 ml-4" />
            <FormField label="Email Address" icon={<Mail size={14} className="text-slate-400" />}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="info@yourbusiness.co.uk"
                className="w-full text-sm text-slate-800 placeholder:text-slate-400 outline-none"
              />
            </FormField>
            <div className="h-px bg-slate-100 ml-4" />
            <FormField label="Website" icon={<Globe size={14} className="text-slate-400" />}>
              <input
                type="url"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://yourbusiness.co.uk"
                className="w-full text-sm text-slate-800 placeholder:text-slate-400 outline-none"
              />
            </FormField>
          </Section>

          <button
            type="button"
            onClick={() => setWantsOrdering(v => !v)}
            className={`w-full flex items-center gap-4 rounded-2xl border-2 px-5 py-4 transition-all ${wantsOrdering ? "border-primary bg-primary/5" : "border-slate-200 bg-white hover:border-slate-300"}`}
          >
            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${wantsOrdering ? "border-primary bg-primary" : "border-slate-300"}`}>
              {wantsOrdering && <svg viewBox="0 0 12 12" className="w-3 h-3 text-white fill-white"><path d="M1.5 6l3.5 3.5 5.5-7" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>}
            </div>
            <div className="text-left">
              <p className={`text-sm font-semibold ${wantsOrdering ? "text-primary" : "text-slate-700"}`}>Enable Online Ordering 🛒</p>
              <p className="text-xs text-slate-400 mt-0.5">Offer food/retail ordering for delivery or collection via Ayrshire Hub</p>
            </div>
          </button>

          <button
            type="submit"
            disabled={isPending || imageUploading}
            className="w-full flex items-center justify-center gap-2.5 bg-amber-500 hover:bg-amber-600 text-white font-bold py-4 rounded-2xl transition-all disabled:opacity-70"
          >
            {imageUploading ? (
              <><span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Uploading image…</>
            ) : isPending ? (
              <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <><Send size={16} /> Submit for Review</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <p className="text-sm font-bold text-slate-700">{title}</p>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {children}
      </div>
    </div>
  );
}

function FormField({ label, required, icon, children, id }: { label: string; required?: boolean; icon?: React.ReactNode; children: React.ReactNode; id?: string }) {
  const fieldId = id ?? label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return (
    <div className="px-4 py-3">
      <label htmlFor={fieldId} className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1.5">
        {label}{required && " *"}
      </label>
      <div className="flex items-center gap-2">
        {icon && <span className="flex-shrink-0" aria-hidden="true">{icon}</span>}
        {React.isValidElement(children) ? React.cloneElement(children as React.ReactElement<{ id?: string }>, { id: fieldId }) : children}
      </div>
    </div>
  );
}
